import {
  all,
  call,
  getContext,
  put,
  takeEvery,
  select,
} from "redux-saga/effects";

import {
  createPostFailure,
  createPostSuccess,
  deletePostFailure,
  deletePostSuccess,
  editPostFailure,
  editPostSuccess,
  getFeedPortionFailure,
  getFeedPortionSuccess,
  getFeedPostsFailure,
  getFeedPostsSuccess,
  getPortionFailure,
  getPortionSuccess,
  getPosts as getPostsRequest,
  getPostsFailure,
  getPostsSuccess,
  likePostSuccess,
  unlikePostSuccess,
  pinPostFailure,
  pinPostSuccess,
  unpinPostFailure,
  unpinPostSuccess,
  getFeedPurchasedPostsFailure,
  getFeedPurchasedPostsSuccess,
  getFeedPurchasedPortionSuccess,
  getFeedPurchasedPortionFailure,
} from "./actions";
import {
  createPostActionTypes,
  deletePostActionTypes,
  editPostActionTypes,
  getFeedPortionActionTypes,
  getFeedPostsActionTypes,
  getPortionActionTypes,
  getPostsActionTypes,
  likePostActionTypes,
  unlikePostActionTypes,
  pinPostActionTypes,
  unPinPostActionTypes,
  getFeedPurchasedPortionActionTypes,
} from "./actionTypes";
import { CONTEXT } from "@const/redux";
import { normalizePost, normalizePostArray } from "@helpers/posts";
import { setAuthors } from "../authors/actions";
import ToastService from "../../services/ToastService";
import { updateSubscriptions } from "../subscriptions/actions";
import { getProfile, incrementPostsCounter } from "../profile/actions";
import { decrementPostsCounter } from "../profile/actions";
import { editScheduledPostSuccess } from "../scheduledPosts/actions";
import { getPostDetails } from "../postDetails/actions";
import { paginationLimit } from "@const/feed";
import { localstorageKeys } from "@const/store";
import { resetActiveStream } from "@redux/livestreams/actions";

const infoToast = ({ ToasterService, info }) =>
  ToasterService.infoTranslated(info, "create-post");

const createPostAsync = ({ PostsService, post }) => {
  if (!PostsService) {
    console.log("Required services does not exist");

    return;
  }

  return PostsService.createPost(post);
};

function* createPost({ payload }) {
  try {
    const { PostsService, ToasterService } = yield getContext(CONTEXT.SERVICES);
    const { files, uploadedFileIds, poster, scheduledDate, ...postModel } =
      payload;

    const isWithFiles = files?.length > 0 || uploadedFileIds?.length > 0;

    const { data } = yield call(createPostAsync, {
      PostsService,
      post: { ...postModel, scheduledDate, files },
    });

    const { result, entities } = normalizePost(data);
    const { authors, subscriptions } = entities;

    if (scheduledDate) {
      yield call(infoToast, {
        ToasterService,
        info: "WILL_BE_SCHEDULED",
      });
    }

    yield all([
      put(createPostSuccess(result)),
      put(resetActiveStream()),
      put(updateSubscriptions({ subscriptions })),
      put(setAuthors(authors)),
    ]);

    if (!isWithFiles && !scheduledDate) {
      yield put(incrementPostsCounter());
    }
    const { username } = yield select(({ profileReducer }) => profileReducer);
    yield put(getPostsRequest({ username, limit: paginationLimit }));
  } catch ({ error, ...rest }) {
    ToastService.error(error.message);
    yield put(createPostFailure(error));
  }
}
export const getPostsAsync = ({ PostsService, username, params }) => {
  if (!PostsService) {
    return;
  }

  return PostsService.getPostsByUsername(username, params);
};

function* getPosts({ payload }) {
  try {
    const { PostsService, StorageService } = yield getContext(CONTEXT.SERVICES);
    const { username, ...params } = payload;
    const { accessToken } = StorageService.get(localstorageKeys.session, {});

    const { data, pagination } = yield call(getPostsAsync, {
      PostsService,
      params,
      username,
    });

    const { entities, result: allPosts } = normalizePostArray(data);

    const { authors, subscriptions, posts: postById } = entities;

    const { totalCount } = pagination;

    yield all([
      put(setAuthors(authors)),
      put(updateSubscriptions({ subscriptions })),
      put(getPostsSuccess({ allPosts, postById, totalCount })),
    ]);

    if (accessToken) {
      yield put(getProfile());
    }
  } catch ({ error }) {
    yield put(getPostsFailure(error));
  }
}

function* getPortion({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);
    const { username, ...params } = payload;

    const { data, pagination } = yield call(getPostsAsync, {
      PostsService,
      params,
      username,
    });

    const { entities, result: allPosts } = normalizePostArray(data);
    const { authors, subscriptions, posts: postById } = entities;
    const { totalCount } = pagination;

    yield all([
      put(updateSubscriptions({ subscriptions })),
      put(setAuthors(authors)),
      put(getPortionSuccess({ allPosts, postById, totalCount })),
    ]);
  } catch ({ error }) {
    yield put(getPortionFailure(error));
  }
}

const deletePostAsync = ({ PostsService, id }) => {
  if (!PostsService) {
    return;
  }

  return PostsService.deletePostById(id);
};

function* deletePost({ payload: { id } }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);

    yield call(deletePostAsync, { PostsService, id });
    yield put(deletePostSuccess({ id }));
    yield put(decrementPostsCounter());
    yield put(getProfile());
  } catch ({ error }) {
    yield put(deletePostFailure(error));
  }
}

const pinPostAsync = ({ PostsService, id }) => {
  if (!PostsService) {
    return;
  }

  return PostsService.pinPost(id);
};

const getPinPostPositionAsync = ({ PostsService, id }) => {
  if (!PostsService) {
    return;
  }

  return PostsService.getPinPostPosition(id);
};

function* pinPost({ payload: { pinId, unpinId } }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);

    yield call(pinPostAsync, { PostsService, id: pinId });

    if (unpinId) {
      const {
        data: { position },
      } = yield call(getPinPostPositionAsync, { PostsService, id: unpinId });

      yield put(unpinPostSuccess({ id: unpinId, position: position - 1 }));
    }
    yield put(pinPostSuccess({ id: pinId }));
  } catch ({ error }) {
    yield put(pinPostFailure(error));
  }
}

function* unpinPost({ payload: { id } }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);

    yield call(pinPostAsync, { PostsService, id });

    const {
      data: { position },
    } = yield call(getPinPostPositionAsync, { PostsService, id });

    yield put(unpinPostSuccess({ id, position: position - 1 }));
  } catch ({ error }) {
    yield put(unpinPostFailure(error));
  }
}

const likePostAsync = ({ PostsService, postId }) => {
  if (!PostsService) {
    console.log("Required services does not exist");
    return;
  }

  return PostsService.likePostById(postId);
};

function* likePost({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);
    const { postId, uniqueKey, isMyPost, creatorId } = payload;

    const {
      data: { likesCount },
    } = yield call(likePostAsync, {
      PostsService,
      postId,
    });

    yield put(
      getPostDetails({
        uniqueKey,
      }),
    );

    yield put(likePostSuccess({ postId, likesCount, isMyPost, creatorId }));
  } catch ({ error }) {
    console.log(error);
  }
}

const unlikePostAsync = ({ PostsService, postId }) => {
  if (!PostsService) {
    console.log("Required services does not exist");
    return;
  }

  return PostsService.unlikePostById(postId);
};

function* unlikePost({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);
    const { postId, uniqueKey, isMyPost, creatorId } = payload;

    const {
      data: { likesCount },
    } = yield call(unlikePostAsync, {
      PostsService,
      postId,
    });

    yield put(
      getPostDetails({
        uniqueKey,
      }),
    );

    yield put(unlikePostSuccess({ postId, likesCount, isMyPost, creatorId }));
  } catch ({ error }) {
    console.log(error);
  }
}

const getFeedPostsAsync = ({ PostsService, params }) => {
  if (!PostsService) {
    return;
  }

  return PostsService.getFeedPosts(params);
};

const getFeedPurchasedPostsAsync = ({ PostsService, params }) => {
  if (!PostsService) {
    return;
  }

  return PostsService.getFeedPosts({ ...params, onlyPurchased: true });
};

function* getFeedPosts({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getFeedPostsAsync, {
      PostsService,
      params: payload,
    });

    const { entities, result: allPosts } = normalizePostArray(data);
    const { authors, subscriptions, posts: postById } = entities;
    const { totalCount } = pagination;

    yield all([
      put(updateSubscriptions({ subscriptions })),
      put(setAuthors(authors)),
      put(
        getFeedPostsSuccess({
          allPosts,
          postById,
          totalCount,
        }),
      ),
    ]);
  } catch ({ error }) {
    yield put(getFeedPostsFailure(error));
  }
}

function* getFeedPurchasedPosts({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getFeedPurchasedPostsAsync, {
      PostsService,
      params: payload,
    });

    const { entities, result: purchasedPosts } = normalizePostArray(data);
    const { authors, subscriptions, posts: postById } = entities;
    const { totalCount } = pagination;

    yield all([
      put(updateSubscriptions({ subscriptions })),
      put(setAuthors(authors)),
      put(
        getFeedPurchasedPostsSuccess({
          purchasedPosts,
          postById,
          purchasedTotalCount: totalCount,
        }),
      ),
    ]);
  } catch ({ error }) {
    yield put(getFeedPurchasedPostsFailure(error));
  }
}

function* getFeedPortion({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getFeedPostsAsync, {
      PostsService,
      params: payload,
    });

    const { entities, result: allPosts } = normalizePostArray(data);
    const { authors, subscriptions, posts: postById } = entities;
    const { totalCount } = pagination;

    yield all([
      put(updateSubscriptions({ subscriptions })),
      put(setAuthors(authors)),
      put(getFeedPortionSuccess({ allPosts, postById, totalCount })),
    ]);
  } catch ({ error }) {
    yield put(getFeedPortionFailure(error));
  }
}

function* getFeedPurchasedPortion({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getFeedPurchasedPostsAsync, {
      PostsService,
      params: payload,
    });

    const { entities, result: purchasedPosts } = normalizePostArray(data);
    const { authors, subscriptions, posts: postById } = entities;
    const { totalCount } = pagination;

    yield all([
      put(updateSubscriptions({ subscriptions })),
      put(setAuthors(authors)),
      put(
        getFeedPurchasedPortionSuccess({
          purchasedPosts,
          postById,
          purchasedTotalCount: totalCount,
        }),
      ),
    ]);
  } catch ({ error }) {
    yield put(getFeedPurchasedPortionFailure(error));
  }
}

const editPostAsync = ({ PostsService, id, ...body }) => {
  if (!PostsService) {
    console.log("Missing required service");
    return;
  }

  return PostsService.editPost(id, body);
};

function* editPost({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);

    const {
      id,
      title,
      contentType,
      price,
      priceType,
      accessType,
      description,
      subscriptionIds,
      consentFormIds,
      scheduledDate,
      scheduledTimeZoneName,
      files,
      isPublished,
      isLiveStreamChatDisplayed,
    } = payload;

    const { data } = yield call(editPostAsync, {
      PostsService,
      ...{
        id,
        files,
        title,
        contentType,
        subscriptionIds,
        consentFormIds,
        price,
        priceType,
        accessType,
        description,
        scheduledDate,
        scheduledTimeZoneName,
        isLiveStreamChatDisplayed,
      },
    });
    const { result, entities } = normalizePost(data);
    const post = entities.posts[result];

    if (isPublished) {
      yield put(editPostSuccess({ post, result }));
    } else {
      yield put(editScheduledPostSuccess({ post, result }));
      yield put(editPostSuccess({ post, result }));
    }
  } catch ({ error }) {
    ToastService.error(error?.message);
    yield put(editPostFailure());
  }
}

export function* watchCreatePost() {
  yield takeEvery(createPostActionTypes.request, createPost);
}

export function* watchGetPosts() {
  yield takeEvery(getPostsActionTypes.request, getPosts);
}

export function* watchGetPortion() {
  yield takeEvery(getPortionActionTypes.request, getPortion);
}

export function* watchDeletePost() {
  yield takeEvery(deletePostActionTypes.request, deletePost);
}

export function* watchPinPost() {
  yield takeEvery(pinPostActionTypes.request, pinPost);
}

export function* watchUnPinPost() {
  yield takeEvery(unPinPostActionTypes.request, unpinPost);
}

export function* watchLikePost() {
  yield takeEvery(likePostActionTypes.request, likePost);
}

export function* watchUnlikePost() {
  yield takeEvery(unlikePostActionTypes.request, unlikePost);
}

export function* watchGetFeedPosts() {
  yield takeEvery(getFeedPostsActionTypes.request, getFeedPosts);
}
export function* watchGetFeedPurchasedPosts() {
  yield takeEvery(getFeedPostsActionTypes.request, getFeedPurchasedPosts);
}

export function* watchGetFeedPortion() {
  yield takeEvery(getFeedPortionActionTypes.request, getFeedPortion);
}

export function* watchGetFeedPurchasedPortion() {
  yield takeEvery(
    getFeedPurchasedPortionActionTypes.request,
    getFeedPurchasedPortion,
  );
}

export function* watchEditPost() {
  yield takeEvery(editPostActionTypes.request, editPost);
}
